import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Toastify from "../../utils/tostfy";
import { endpoint } from "../../utils/endpoints";
import {
  normalizePastedSearchInput,
  normalizeSearchInput,
  SEARCH_MAX_LENGTH,
} from "../../utils/searchInput";
import LeftAngle from "../../assets/image/left-angle.svg";
import RightAngle from "../../assets/image/right-angle.svg";
import DownArrow from "../../assets/image/down-arrow.svg";
import { useListPersistence } from "../../hooks/useListPersistence";
import { readListLimit, readListPage } from "../../hooks/useListQueryPersistence";
import {
  formatBalanceRupees,
  formatInr,
  formatSummaryRupees,
} from "../../utils/formatAmount";
import PageNumberInput from "../../Components/Pagination/PageNumberInput";

const LIST_PATH = "/salary-disbursement";

const ACCOUNTANT_API =
  process.env.REACT_APP_ACCOUNTANT_API;

/** Theme (from design spec) */
const C = {
  bg: "#FFFFFF",
  card: "#F5F5F5",
  thead: "#000053",
  text: "#000000",
  muted: "#757575",
  border: "#E0E0E0",
};

const BALANCE_FILTER_OPTIONS = [
  { value: "all", label: "All Drivers" },
  { value: "positive_balance", label: "Positive Balance" },
  { value: "negative_balance", label: "Negative Balance" },
  { value: "zero_balance", label: "Zero Balance" },
];

const normalizeBalanceFilter = (raw) => {
  const legacyMap = {
    positive: "positive_balance",
    negative: "negative_balance",
    zero: "zero_balance",
  };
  const value = String(raw || "all").trim();
  if (value === "all") return "all";
  return legacyMap[value] || value;
};

const readBalanceFilterFromParams = (searchParams) =>
  normalizeBalanceFilter(
    searchParams.get("balance_status") || searchParams.get("balance")
  );

const parseBody = async (response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { rawMessage: text };
  }
};

const getErrorMessage = (payload, response) => {
  if (!payload || typeof payload !== "object") {
    return response.statusText || "Something went wrong. Please try again.";
  }
  return (
    payload.errorMessage ||
    payload.message ||
    payload.error ||
    (Array.isArray(payload.errors) ? payload.errors.join(", ") : null) ||
    `Request failed (${response.status})`
  );
};

function formatMobileDisplay(digits) {
  const d = String(digits || "").replace(/\D/g, "");
  if (d.length === 10) {
    return `${d.slice(0, 3)} ${d.slice(3, 7)} ${d.slice(7)}`;
  }
  return digits || "—";
}

function formatLastPayment(isoOrDate) {
  if (!isoOrDate) return "—";
  const d = isoOrDate instanceof Date ? isoOrDate : new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatSummaryCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-IN");
}

const mapSalaryDriverRow = (d) => ({
  id: d._id,
  driverRefId:
    d.driver_ref_id != null && d.driver_ref_id !== "" ? `${d.driver_ref_id}` : "—",
  driverName: d.full_name ?? "—",
  license: d.license_number ?? "—",
  mobile: d.mobile_number ?? "",
  balanceRupees: Number(d.current_balance) || 0,
  lastPayment: d.last_payment || "",
  tripsThisMonth: Number(d.total_trip) || 0,
});

const MIN_MONTH_VALUE = "2026-04";
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const toMonthValue = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const formatMonthLabel = (monthValue) => {
  const { year, month } = parseMonthYear(monthValue);
  if (!year || !month) return "";
  const d = new Date(Number(year), Number(month) - 1, 1);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
};

function parseMonthYear(monthValue) {
  const [year, month] = String(monthValue || "").split("-");
  return {
    year: year || "",
    month: month || "",
  };
}

const isMonthSelectable = (year, month, minYear, minMonth, maxYear, maxMonth) => {
  if (year < minYear || year > maxYear) return false;
  if (year === minYear && month < minMonth) return false;
  if (year === maxYear && month > maxMonth) return false;
  return true;
};

const SalaryDisbursement = () => {
  const [searchParams] = useSearchParams();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [apiTotalPages, setApiTotalPages] = useState(1);
  const [summary, setSummary] = useState({
    totalDrivers: 0,
    totalOutstanding: 0,
    totalNegative: 0,
    paymentThisMonth: 0,
    totalPayoutAmount: 0,
    totalAwardAmount: 0,
  });

  const [balanceFilter, setBalanceFilter] = useState(() =>
    readBalanceFilterFromParams(searchParams)
  );
  const [search, setSearch] = useState(() => searchParams.get("search") || "");
  const [debouncedSearch, setDebouncedSearch] = useState(() =>
    (searchParams.get("search") || "").trim()
  );

  const [monthValue, setMonthValue] = useState(() => {
    const fromUrl = searchParams.get("month");
    if (fromUrl) return fromUrl;
    const d = new Date();
    return toMonthValue(d);
  });
  const [monthPickerOpen, setMonthPickerOpen] = useState(false);
  const [monthPickerYear, setMonthPickerYear] = useState(() =>
    Number(parseMonthYear(searchParams.get("month") || toMonthValue(new Date())).year) ||
      new Date().getFullYear()
  );
  const [currentPage, setCurrentPage] = useState(() => readListPage(searchParams));
  const [itemsPerPage, setItemsPerPage] = useState(() => readListLimit(searchParams));
  const monthPickerRef = useRef(null);

  const applyRestoredState = useCallback((saved) => {
    if (saved.search != null) {
      setSearch(saved.search);
      setDebouncedSearch(String(saved.search).trim());
    }
    if (saved.page) {
      const p = Number(saved.page);
      if (Number.isFinite(p) && p > 0) setCurrentPage(p);
    }
    if (saved.limit) {
      const l = Number(saved.limit);
      if (Number.isFinite(l) && l > 0) setItemsPerPage(l);
    }
    if (saved.balance_status) {
      setBalanceFilter(normalizeBalanceFilter(saved.balance_status));
    } else if (saved.balance) {
      setBalanceFilter(normalizeBalanceFilter(saved.balance));
    }
    if (saved.month) setMonthValue(saved.month);
  }, []);

  const { detailListQuery } = useListPersistence(
    LIST_PATH,
    {
      page: currentPage,
      limit: itemsPerPage,
      search: debouncedSearch || undefined,
      balance_status: balanceFilter !== "all" ? balanceFilter : undefined,
      month: monthValue || undefined,
    },
    applyRestoredState
  );

  const maxMonthValue = useMemo(() => toMonthValue(new Date()), []);
  const selectedMonthLabel = useMemo(
    () => formatMonthLabel(monthValue) || "Selected month",
    [monthValue]
  );
  /** Summary cards show one month before the filter selection (e.g. June → May). */
  const summaryMonthLabel = useMemo(() => {
    const { year, month } = parseMonthYear(monthValue);
    const y = Number(year);
    const m = Number(month);
    if (!y || !m) return "";
    return new Date(y, m - 2, 1).toLocaleDateString("en-IN", {
      month: "long",
      year: "numeric",
    });
  }, [monthValue]);
  const minYear = Number(parseMonthYear(MIN_MONTH_VALUE).year) || 2026;
  const minMonth = Number(parseMonthYear(MIN_MONTH_VALUE).month) || 4;
  const currentYear = Number(parseMonthYear(maxMonthValue).year) || new Date().getFullYear();
  const currentMonth = Number(parseMonthYear(maxMonthValue).month) || new Date().getMonth() + 1;
  const selectedYear = Number(parseMonthYear(monthValue).year) || currentYear;
  const canGoPrevYear = monthPickerYear > minYear;
  const canGoNextYear = monthPickerYear < currentYear;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!monthPickerOpen) return undefined;
    const onMouseDown = (event) => {
      if (monthPickerRef.current && !monthPickerRef.current.contains(event.target)) {
        setMonthPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [monthPickerOpen]);

  useEffect(() => {
    let cancelled = false;

    const loadDriverSalary = async () => {
      const token = localStorage.getItem("sessionToken");
      if (!token) {
        toast.error("Session expired. Please log in again.");
        setDrivers([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      const { year, month } = parseMonthYear(monthValue);
      const params = new URLSearchParams({
        page: String(currentPage),
        limit: String(itemsPerPage),
        search: debouncedSearch.trim(),
        month,
        year,
      });
      if (balanceFilter !== "all") {
        params.set("balance_status", balanceFilter);
      }

      const url = `${ACCOUNTANT_API}${endpoint.listOfDriverSalary}?${params.toString()}`;

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const payload = await parseBody(response);
        if (cancelled) return;

        if (!response.ok) {
          toast.error(getErrorMessage(payload, response));
          setDrivers([]);
          setTotalRecords(0);
          setApiTotalPages(1);
          return;
        }

        const apiCode = payload?.code;
        if (apiCode != null && apiCode !== 200 && apiCode !== 201) {
          toast.error(getErrorMessage(payload, response));
          setDrivers([]);
          setTotalRecords(0);
          setApiTotalPages(1);
          return;
        }

        const data = payload?.data || {};
        const list = Array.isArray(data.driver_data) ? data.driver_data : [];

        setDrivers(list.map(mapSalaryDriverRow));
        setTotalRecords(typeof data.total === "number" ? data.total : list.length);
        setApiTotalPages(
          typeof data.total_pages === "number" && data.total_pages > 0
            ? data.total_pages
            : 1
        );
        setSummary({
          totalDrivers: data.total_no_of_driver ?? 0,
          totalOutstanding: data.total_outstanding_balance ?? 0,
          totalNegative: data.total_negative ?? 0,
          paymentThisMonth: data.payment_of_this_month ?? 0,
          totalPayoutAmount: data.total_payout_amount ?? 0,
          totalAwardAmount: data.total_award_amount ?? 0,
        });
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          toast.error(
            err?.message === "Failed to fetch"
              ? "Network error. Check your connection."
              : "Could not load driver salary data."
          );
          setDrivers([]);
          setTotalRecords(0);
          setApiTotalPages(1);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadDriverSalary();
    return () => {
      cancelled = true;
    };
  }, [currentPage, itemsPerPage, monthValue, debouncedSearch, balanceFilter]);

  const indexOfFirst = (currentPage - 1) * itemsPerPage;
  const totalPages = apiTotalPages;

  const totalSalary = Number(summary.totalPayoutAmount ?? 0);
  const awardBalance = Number(summary.totalAwardAmount ?? 0);
  const totalNegative = Math.abs(Number(summary.totalNegative) || 0);
  const negativeBalanceAmount = totalNegative > 0 ? -totalNegative : 0;

  const summaryCards = [
    {
      label: "Total Drivers",
      value: formatSummaryCount(summary.totalDrivers),
      sub: "Active",
    },
    {
      label: "Total Outstanding Balance",
      value: formatSummaryRupees(summary.totalOutstanding),
      sub: "Across all drivers",
    },
    {
      label: "Negative Balance",
      value:
        totalNegative > 0
          ? formatInr(negativeBalanceAmount)
          : formatSummaryRupees(0),
      rawAmount: totalNegative > 0 ? negativeBalanceAmount : undefined,
      sub: "Drivers need attention",
      valueRed: true,
    },
    {
      label: "Payment This Month",
      value: formatSummaryRupees(summary.paymentThisMonth),
      sub: summaryMonthLabel || "Selected month",
    },
    {
      label: "Total Salary",
      value: formatBalanceRupees(totalSalary),
      rawAmount: totalSalary,
      sub: summaryMonthLabel || "Selected month",
    },
    {
      label: "Award Balance",
      value: formatBalanceRupees(awardBalance),
      rawAmount: awardBalance,
      sub: "",
    },
  ];

  const handleSearchChange = (value) => {
    setSearch(normalizeSearchInput(value));
    setCurrentPage(1);
  };

  const handleSearchPaste = (event) => {
    event.preventDefault();
    setSearch(normalizePastedSearchInput(event.clipboardData.getData("text")));
    setCurrentPage(1);
  };

  return (
    <>
      <div
        className="tab-pane-content mt-7 font-UbuntuRegular"
        style={{ borderRadius: "10px" }}
      >
        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4 mb-8">
          {summaryCards.map((card) => (
            <div
              key={card.label}
              className="rounded-xl px-5 py-4 border bg-white shadow-sm border-[#E0E0E0]"
              style={{ backgroundColor: C.card }}
            >
              <p className="text-sm mb-1" style={{ color: C.muted }}>
                {card.label}
              </p>
              <p
                className={`text-2xl font-UbuntuBold mb-1 ${
                  card.valueRed || (card.rawAmount != null && Number(card.rawAmount) < 0)
                    ? "text-red-600"
                    : ""
                }`}
                style={
                  card.valueRed || (card.rawAmount != null && Number(card.rawAmount) < 0)
                    ? undefined
                    : { color: C.text }
                }
              >
                {card.value}
              </p>
              {card.sub ? (
                <p className="text-xs" style={{ color: C.muted }}>
                  {card.sub}
                </p>
              ) : null}
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-4 lg:gap-6 mb-6">
          <div className="min-w-[160px] flex-1 sm:flex-none">
            <label
              className="block text-xs font-UbuntuMedium mb-1.5"
              style={{ color: C.muted }}
              htmlFor="balance-status"
            >
              Balance Status
            </label>
            <select
              id="balance-status"
              className="form-control h-11 rounded-lg border text-sm w-full max-w-xs"
              style={{ borderColor: C.border, color: C.text }}
              value={balanceFilter}
              onChange={(e) => {
                setBalanceFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              {BALANCE_FILTER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1 min-w-[200px] max-w-md">
            <label
              className="block text-xs font-UbuntuMedium mb-1.5"
              style={{ color: C.muted }}
              htmlFor="driver-search"
            >
              Search Driver
            </label>
            <input
              id="driver-search"
              type="text"
              placeholder="Search by name or ID..."
              className="border px-3 py-2.5 rounded-lg outline-none text-sm searchIcon bg-white focus:!shadow-sm w-full h-11"
              style={{ borderColor: C.border }}
              value={search}
              maxLength={SEARCH_MAX_LENGTH}
              onChange={(e) => handleSearchChange(e.target.value)}
              onPaste={handleSearchPaste}
              autoComplete="off"
            />
          </div>

          <div className="relative" ref={monthPickerRef}>
            <label
              className="block text-xs font-UbuntuMedium mb-1.5"
              style={{ color: C.muted }}
              htmlFor="salary-month-trigger"
            >
              Month
            </label>
            <button
              id="salary-month-trigger"
              type="button"
              className="form-control h-11 min-w-[180px] rounded-lg border bg-white px-3 text-left text-sm w-full flex items-center justify-between gap-2"
              style={{ borderColor: C.border, color: C.text }}
              onClick={() => {
                setMonthPickerYear(selectedYear);
                setMonthPickerOpen((s) => !s);
              }}
              aria-expanded={monthPickerOpen}
              aria-haspopup="dialog"
            >
              <span className="truncate">{selectedMonthLabel || "Select month"}</span>
              <img src={DownArrow} alt="" className="h-3 w-3 shrink-0 opacity-60" />
            </button>
            {monthPickerOpen ? (
              <div
                className="absolute left-0 top-[calc(100%+4px)] z-30 min-w-[240px] rounded-lg border bg-white p-4 shadow-lg"
                style={{ borderColor: C.border }}
                role="dialog"
                aria-label="Choose month and year"
              >
                <div className="mb-4 flex items-center justify-between">
                  <button
                    type="button"
                    className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30"
                    disabled={!canGoPrevYear}
                    aria-label="Previous year"
                    onClick={() => canGoPrevYear && setMonthPickerYear((y) => y - 1)}
                  >
                    <img src={LeftAngle} className="h-3 w-3" alt="" />
                  </button>
                  <span className="text-sm font-UbuntuMedium" style={{ color: C.text }}>
                    {monthPickerYear}
                  </span>
                  <button
                    type="button"
                    className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30"
                    disabled={!canGoNextYear}
                    aria-label="Next year"
                    onClick={() => canGoNextYear && setMonthPickerYear((y) => y + 1)}
                  >
                    <img src={RightAngle} className="h-3 w-3" alt="" />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {MONTH_NAMES.map((name, index) => {
                    const month = index + 1;
                    const monthKey = `${monthPickerYear}-${String(month).padStart(2, "0")}`;
                    const isSelected = monthKey === monthValue;
                    const isActive = isMonthSelectable(
                      monthPickerYear,
                      month,
                      minYear,
                      minMonth,
                      currentYear,
                      currentMonth
                    );
                    return (
                      <button
                        key={monthKey}
                        type="button"
                        disabled={!isActive}
                        className={`h-9 rounded-full text-sm font-UbuntuMedium transition-colors ${
                          isSelected
                            ? "text-white"
                            : isActive
                              ? "text-black hover:bg-gray-100"
                              : "cursor-not-allowed text-gray-300"
                        }`}
                        style={{
                          backgroundColor: isSelected ? C.thead : undefined,
                        }}
                        onClick={() => {
                          if (!isActive) return;
                          setMonthValue(monthKey);
                          setCurrentPage(1);
                          setMonthPickerOpen(false);
                        }}
                      >
                        {name.slice(0, 3)}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>

          <div className="w-full sm:w-auto sm:ml-auto flex justify-end">
            <Link
              to="/salary-disbursement/salary-and-awards-detail/view-transactions"
              className="inline-flex items-center justify-center h-11 px-5 rounded-full text-sm font-UbuntuMedium border bg-white hover:bg-gray-50 transition-colors"
              style={{ borderColor: C.border, color: C.text }}
            >
              Payment History
            </Link>
          </div>
        </div>

        {/* Table */}
        <div
          className="common-table-wrapper m-0 rounded-xl border shadow-sm"
          style={{ borderColor: C.border }}
        >
          <div className="m-0 w-full overflow-x-auto">
            <table className="mb-0 w-full min-w-[1250px] bg-white">
              <thead>
                <tr style={{ backgroundColor: C.thead }}>
                  <th className="!text-white !font-UbuntuMedium text-xs md:text-sm whitespace-nowrap px-3 py-3.5 border-0 text-center">
                    S.No.
                  </th>
                  <th className="!text-white !font-UbuntuMedium text-xs md:text-sm whitespace-nowrap px-3 py-3.5 border-0 text-center">
                    Driver ID
                  </th>
                  <th className="!text-white !font-UbuntuMedium text-xs md:text-sm whitespace-nowrap px-3 py-3.5 border-0 text-center">
                    Driver Name
                  </th>
                  <th className="!text-white !font-UbuntuMedium text-xs md:text-sm whitespace-nowrap px-3 py-3.5 border-0 text-center">
                    Driver License Number
                  </th>
                  <th className="!text-white !font-UbuntuMedium text-xs md:text-sm whitespace-nowrap px-3 py-3.5 border-0 text-center">
                    Mobile Number
                  </th>
                  <th className="!text-white !font-UbuntuMedium text-xs md:text-sm whitespace-nowrap px-3 py-3.5 border-0 text-center">
                    Current Remaining Amount
                  </th>
                  <th className="!text-white !font-UbuntuMedium text-xs md:text-sm whitespace-nowrap px-3 py-3.5 border-0 text-center">
                    Last Payment
                  </th>
                  <th className="!text-white !font-UbuntuMedium text-xs md:text-sm whitespace-nowrap px-3 py-3.5 border-0 text-center ">
                    Trips (This Month)
                  </th>
                  <th className="!text-white !font-UbuntuMedium text-xs md:text-sm whitespace-nowrap px-3 py-3.5 border-0 text-center">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr className="text-center">
                    <td
                      colSpan={9}
                      className="text-center py-10 text-sm"
                      style={{ color: C.muted }}
                    >
                      Loading drivers…
                    </td>
                  </tr>
                ) : drivers.length === 0 ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="text-center py-10 text-sm"
                      style={{ color: C.muted }}
                    >
                      No drivers match your filters.
                    </td>
                  </tr>
                ) : (
                  drivers.map((driver, index) => (
                    <tr
                      key={driver.id}
                      className={index % 2 === 0 ? "bg-white text-center" : "bg-[#FAFAFA] text-center" }
                      style={{ borderColor: C.border }}
                    >
                      <td className="align-middle px-3 py-3 text-sm text-black whitespace-nowrap text-center">
                        {String(indexOfFirst + index + 1).padStart(2, "0")}
                      </td>
                      <td className="align-middle px-3 py-3 text-sm text-center text-black whitespace-nowrap">
                        {driver.driverRefId}
                      </td>
                      <td className="align-middle px-3 py-3 text-sm font-medium text-black text-center whitespace-nowrap">
                        {driver.driverName}
                      </td>
                      <td className="align-middle px-3 py-3 text-sm text-black text-center whitespace-nowrap">
                        {driver.license}
                      </td>
                      <td className="align-middle px-3 py-3 text-sm text-black whitespace-nowrap text-center">
                        {formatMobileDisplay(driver.mobile)}
                      </td>
                      <td className="align-middle px-3 py-3 text-sm whitespace-nowrap font-medium text-center">
                        <span
                          className={
                            driver.balanceRupees < 0 ? "text-red-600" : "text-black"
                          }
                        >
                          {formatBalanceRupees(driver.balanceRupees, {
                            suffix: "/-",
                            symbolSpace: false,
                          })}
                        </span>
                      </td>
                      <td className="align-middle px-3 py-3 text-sm text-black whitespace-nowrap text-center">
                        {formatLastPayment(driver.lastPayment)}
                      </td>
                      <td className="align-middle px-3 py-3 text-sm text-black whitespace-nowrap text-center">
                        {driver.tripsThisMonth} Trips
                      </td>
                      <td className="align-middle px-3 py-3 whitespace-nowrap text-center">
                        <div className="flex flex-nowrap items-center justify-center gap-2">
                          <Link
                            to={`/salary-disbursement/salary-and-awards-detail/salary-disburse?driver_id=${encodeURIComponent(driver.id)}${detailListQuery}`}
                            className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-full px-4 py-2 text-xs font-UbuntuMedium transition-colors"
                            style={{
                              backgroundColor: "#198615",
                              color: C.card,
                              border: `1px solid ${C.border}`,
                            }}
                          >
                            Pay Salary
                          </Link>
                          <Link
                            to={`/salary-disbursement/salary-and-awards-detail?driver_id=${encodeURIComponent(driver.id)}${detailListQuery}`}
                            className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-full px-4 py-2 text-xs font-UbuntuMedium transition-colors hover:bg-gray-50 text-white"
                            style={{
                              color: C.text,
                              backgroundColor: "#f7a220",
                              border: `1px solid ${C.border}`,
                            }}
                          >
                            View Ledger
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="flex gap-x-4 items-center mt-4 flex-wrap">
          <select
            className="p-2 shadow-sm outline-none rounded-lg border"
            style={{ borderColor: C.border }}
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            disabled={loading}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
          </select>
          <div className="text-sm ml-auto" style={{ color: C.muted }}>
            {totalRecords} records found
          </div>
          <div className="flex items-center gap-2 pr-2 md:pr-8">
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1 || loading}
              className="!bg-primary text-white w-[35px] h-[35px] rounded-full disabled:opacity-50 inline-flex justify-center items-center"
            >
              <img src={LeftAngle} className="invert grayscale brightness-100" alt="" />
            </button>
            <PageNumberInput
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              disabled={loading}
              className="h-[35px] w-12 rounded border border-gray-300 text-center text-sm font-UbuntuMedium shadow-sm outline-none disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70"
            />
            <span className="text-sm text-gray-600">of {totalPages}</span>
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || loading}
              className="!bg-primary text-white w-[35px] h-[35px] rounded-full disabled:opacity-50 inline-flex justify-center items-center"
            >
              <img src={RightAngle} className="invert grayscale brightness-100" alt="" />
            </button>
          </div>
        </div>
      </div>

      <Toastify />
    </>
  );
};

export default SalaryDisbursement;
